import { useState } from 'react';
import SEOHead from '../../components/base/SEOHead';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function AgendamentoPage() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    servico: '',
    data: '',
    hora: '',
    mensagem: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Agendamento de Serviços FF TRAVEL",
    "description": "Agende consultas e serviços na FF TRAVEL: viagens internacionais, compra de telemóveis iPhone e transações de ouro em Luanda Angola",
    "url": "https://fftravel.ao/agendamento",
    "provider": {
      "@type": "TravelAgency",
      "name": "FF TRAVEL",
      "telephone": ["+244936769113", "+244927606910"],
      "email": "fftravel437@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua Rainha Ginga, Edifício Atlântico, 3º Andar",
        "addressLocality": "Luanda",
        "addressCountry": "AO"
      }
    },
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://fftravel.ao/agendamento",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      "result": {
        "@type": "Reservation",
        "name": "Agendamento de Serviço FF TRAVEL"
      }
    },
    "offers": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Serviços FF TRAVEL",
        "description": "Consultas para viagens internacionais, venda de telemóveis iPhone e compra/venda de ouro"
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('agendamentos_fftravel')
        .insert([
          {
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            servico: formData.servico,
            data: formData.data,
            hora: formData.hora,
            mensagem: formData.mensagem,
            status: 'pendente'
          }
        ]);

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        servico: '',
        data: '',
        hora: '',
        mensagem: '',
      });

      // Esconder mensagem de sucesso após 5 segundos
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Erro ao enviar agendamento:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Agendamento de Serviços - FF TRAVEL | Marque Consulta em Luanda Angola"
        description="Agende consulta na FF TRAVEL em Luanda para viagens internacionais, compra de iPhone ou transações de ouro. Atendimento personalizado com hora marcada. Telefones: +244 936 769 113 / +244 927 606 910. Rua Rainha Ginga, Edifício Atlântico."
        canonical="https://fftravel.ao/agendamento"
        keywords="agendamento FF Travel, marcar consulta Luanda, reserva atendimento Angola, agendar viagem internacional, consulta compra iPhone, agendamento venda ouro"
        ogImage="https://public.readdy.ai/ai/img_res/7a638173ec3179c714ed57503ea7648a.jpg"
        schemaData={schemaData}
      />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20professional%20consultation%20room%20in%20Luanda%20Angola%20elegant%20meeting%20space%20comfortable%20seating%20contemporary%20interior%20business%20appointment%20setting&width=1920&height=1080&seq=fftravel-agendamento-hero&orientation=landscape"
            alt="Sala de consulta FF Travel Luanda - espaço moderno para agendamento de serviços e atendimento personalizado"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Agende o Seu Atendimento</h1>
          <p className="text-xl md:text-2xl font-light">Atendimento personalizado com hora marcada</p>
        </div>
      </section>

      {/* Formulário de Agendamento */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">Marque a Sua Consulta</h2>
              <p className="text-lg text-gray-600">
                Preencha o formulário abaixo e entraremos em contacto para confirmar o seu agendamento
              </p>
            </div>

            {/* Mensagem de Sucesso */}
            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-green-50 border-2 border-green-500 rounded-xl animate-fade-in">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-green-800 text-lg">Agendamento enviado com sucesso!</h3>
                    <p className="text-green-700">Entraremos em contacto em breve para confirmar.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Mensagem de Erro */}
            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-red-50 border-2 border-red-500 rounded-xl animate-fade-in">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                    <i className="ri-close-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-red-800 text-lg">Erro ao enviar agendamento</h3>
                    <p className="text-red-700">Por favor, tente novamente ou contacte-nos diretamente.</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome */}
              <div>
                <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="O seu nome completo"
                />
              </div>

              {/* Email e Telefone */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="seuemail@exemplo.com"
                  />
                </div>
                <div>
                  <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="+244 XXX XXX XXX"
                  />
                </div>
              </div>

              {/* Serviço */}
              <div>
                <label htmlFor="servico" className="block text-sm font-semibold text-gray-700 mb-2">
                  Serviço Pretendido *
                </label>
                <select
                  id="servico"
                  name="servico"
                  value={formData.servico}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Selecione um serviço</option>
                  <option value="viagens">Viagens Internacionais</option>
                  <option value="iphone">Compra de iPhone</option>
                  <option value="ouro-compra">Compra de Ouro</option>
                  <option value="ouro-venda">Venda de Ouro</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              {/* Data e Hora */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="data" className="block text-sm font-semibold text-gray-700 mb-2">
                    Data Preferida *
                  </label>
                  <input
                    type="date"
                    id="data"
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label htmlFor="hora" className="block text-sm font-semibold text-gray-700 mb-2">
                    Hora Preferida *
                  </label>
                  <select
                    id="hora"
                    name="hora"
                    value={formData.hora}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Selecione uma hora</option>
                    <option value="08:00">08:00</option>
                    <option value="09:00">09:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="12:00">12:00</option>
                    <option value="14:00">14:00</option>
                    <option value="15:00">15:00</option>
                    <option value="16:00">16:00</option>
                    <option value="17:00">17:00</option>
                  </select>
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-2">
                  Mensagem Adicional
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Informações adicionais sobre o seu pedido (máximo 500 caracteres)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.mensagem.length}/500 caracteres
                </p>
              </div>

              {/* Botão Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4 rounded-lg font-bold text-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    A enviar...
                  </span>
                ) : (
                  'Confirmar Agendamento'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Informações Adicionais */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Informações Úteis</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Horário */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Horário de Atendimento</h3>
              <p className="text-gray-600">
                <strong>Segunda a Sexta:</strong> 08h - 18h<br />
                <strong>Sábado:</strong> 09h - 13h<br />
                <strong>Domingo:</strong> Fechado
              </p>
            </div>

            {/* Confirmação */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-double-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Confirmação</h3>
              <p className="text-gray-600">
                Receberá uma confirmação por email e telefone nas próximas 24 horas após o envio do pedido.
              </p>
            </div>

            {/* Cancelamento */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-close-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Cancelamento</h3>
              <p className="text-gray-600">
                Para cancelar ou reagendar, contacte-nos com pelo menos 24 horas de antecedência.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
